import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from "@material-ui/icons";
import { withAlert } from "react-alert";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import Button from "../../../waybee-ui/Button";
import Dialog from "../../../waybee-ui/Dialog";
import DialogTitle from "../../../waybee-ui/Dialog/DialogTitle";
import DialogContent from "../../../waybee-ui/Dialog/DialogContent";
import DialogActions from "../../../waybee-ui/Dialog/DialogActions";
import AclManagementService from "../../../services/AclManagementService";
import RoleFormDialog from "./RoleFormDialog";
import { getCounterpartCode } from "./buildPermissionTree";

const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`;

const ToolbarText = styled.div`
  flex: 1;
  min-width: 0;
`;

const SectionTitle = styled(Typography)`
  && {
    font-weight: 600;
    font-size: 18px;
    color: #333;
  }
`;

const SectionHint = styled(Typography)`
  && {
    color: #777;
    margin-top: 4px;
    font-size: 14px;
  }
`;

const RolesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const RoleCard = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 148px;
  padding: 18px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.color.primary.main};
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  }
`;

const RoleCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const RoleName = styled(Typography)`
  && {
    font-weight: 600;
    font-size: 16px;
    color: #2f2f2f;
    word-break: break-word;
  }
`;

const RoleDescription = styled(Typography)`
  && {
    color: #666;
    margin-top: 8px;
    font-size: 14px;
    line-height: 1.4;
    flex: 1;
  }
`;

const RoleFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`;

const MetaChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  border: 1px dashed #d8d8d8;
  border-radius: 8px;
  background: #fafafa;
`;

const EmptyTitle = styled(Typography)`
  && {
    font-weight: 600;
    margin-bottom: 6px;
  }
`;

class AccessControl extends Component {
  constructor(props) {
    super(props);
    this.state = {
      roles: [],
      permissions: [],
      loading: false,
      formOpen: false,
      editingRole: null,
      deleteRole: null,
      deleting: false
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    const { alert, t } = this.props;
    this.setState({ loading: true });
    try {
      const [roles, permissions] = await Promise.all([
        AclManagementService.getRoles(true),
        AclManagementService.getPermissions()
      ]);
      this.setState({
        roles: Array.isArray(roles) ? roles : [],
        permissions: Array.isArray(permissions) ? permissions : [],
        loading: false
      });
    } catch (e) {
      console.error(e);
      alert.show(t("settings.profiles.loadError"), {
        title: t("common.actions.error")
      });
      this.setState({ loading: false, roles: [], permissions: [] });
    }
  };

  openCreate = () => {
    this.setState({ formOpen: true, editingRole: null });
  };

  openEdit = role => {
    this.setState({ formOpen: true, editingRole: role });
  };

  closeForm = () => {
    this.setState({ formOpen: false, editingRole: null });
  };

  confirmDelete = role => {
    this.setState({ deleteRole: role });
  };

  closeDelete = () => {
    this.setState({ deleteRole: null, deleting: false });
  };

  handleDelete = async () => {
    const { deleteRole } = this.state;
    const { alert, t } = this.props;
    if (!deleteRole) return;

    this.setState({ deleting: true });
    try {
      await AclManagementService.deleteRole(deleteRole.id);
      alert.show(t("settings.profiles.deleted"), {
        title: t("common.actions.success")
      });
      this.closeDelete();
      this.loadData();
    } catch (e) {
      console.error(e);
      const message =
        (e.response &&
          e.response.data &&
          e.response.data.error &&
          e.response.data.error.message) ||
        t("settings.profiles.deleteError");
      alert.show(message, { title: t("common.actions.error") });
      this.setState({ deleting: false });
    }
  };

  renderRolesContent = () => {
    const { roles, loading } = this.state;
    const { t } = this.props;

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      );
    }

    if (roles.length === 0) {
      return (
        <EmptyState>
          <EmptyTitle>{t("settings.profiles.empty")}</EmptyTitle>
          <Typography color="textSecondary">
            {t("settings.profiles.emptyHint")}
          </Typography>
          <Box mt={2}>
            <Button
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.openCreate}
            >
              {t("settings.profiles.new")}
            </Button>
          </Box>
        </EmptyState>
      );
    }

    return (
      <RolesGrid>
        {roles.map(role => {
          const counterpart = getCounterpartCode(role);
          return (
            <RoleCard key={role.id}>
              <RoleCardHeader>
                <RoleName>{role.name}</RoleName>
                <Actions>
                  <Tooltip title={t("settings.profiles.edit")}>
                    <IconButton
                      size="small"
                      aria-label={t("settings.profiles.editAria")}
                      onClick={() => this.openEdit(role)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      role.isSystem
                        ? t("settings.profiles.cannotDeleteSystem")
                        : t("settings.profiles.delete")
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t("settings.profiles.deleteAria")}
                        onClick={() => this.confirmDelete(role)}
                        disabled={!!role.isSystem}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Actions>
              </RoleCardHeader>

              <RoleDescription>
                {role.description || t("settings.profiles.noDescription")}
              </RoleDescription>

              <RoleFooter>
                <MetaChips>
                  <Chip
                    size="small"
                    label={
                      counterpart
                        ? t("settings.profiles.internalCode", {
                            code: counterpart
                          })
                        : t("settings.profiles.noInternalCode")
                    }
                    variant="outlined"
                  />
                  {role.isSystem && (
                    <Chip
                      size="small"
                      label={t("settings.profiles.system")}
                      color="primary"
                    />
                  )}
                </MetaChips>
              </RoleFooter>
            </RoleCard>
          );
        })}
      </RolesGrid>
    );
  };

  render() {
    const {
      permissions,
      formOpen,
      editingRole,
      deleteRole,
      deleting,
      roles,
      loading
    } = this.state;
    const { t } = this.props;
    const deleteRoleName = deleteRole ? deleteRole.name : "";

    return (
      <Box>
        <Toolbar>
          <ToolbarText>
            <SectionTitle>{t("settings.profiles.title")}</SectionTitle>
            <SectionHint>{t("settings.profiles.help")}</SectionHint>
          </ToolbarText>
          {!(loading || roles.length === 0) && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.openCreate}
            >
              {t("settings.profiles.new")}
            </Button>
          )}
        </Toolbar>

        {this.renderRolesContent()}

        {formOpen && (
          <RoleFormDialog
            open={formOpen}
            role={editingRole}
            permissions={permissions}
            onClose={this.closeForm}
            onSaved={this.loadData}
          />
        )}

        <Dialog open={!!deleteRole} onClose={this.closeDelete}>
          <DialogTitle>{t("settings.profiles.deleteTitle")}</DialogTitle>
          <DialogContent>
            <Typography>
              {t("settings.profiles.deleteConfirm")}
              {" "}
              <strong>{deleteRoleName}</strong>
              ?
              {" "}
              {t("settings.profiles.deleteNote")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              color="secondary"
              onClick={this.closeDelete}
              disabled={deleting}
            >
              {t("common.actions.cancel")}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={this.handleDelete}
              disabled={deleting}
            >
              {deleting
                ? t("common.actions.deleting")
                : t("common.actions.delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

AccessControl.propTypes = {
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withTranslation()(AccessControl));
